// src/merchant/MerchantDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, query, where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  ShoppingBag, Package, TrendingUp, IndianRupee,
  ArrowRight, Clock, CheckCircle, Truck, AlertCircle,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <span className="text-gray-400 font-body text-sm">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className="font-display font-bold text-2xl text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 font-body mt-1">{sub}</p>}
    </div>
  );
}

const STATUS_COLORS = {
  confirmed:  'bg-blue-100 text-blue-600',
  processing: 'bg-amber-100 text-amber-600',
  shipped:    'bg-purple-100 text-purple-600',
  delivered:  'bg-green-100 text-green-600',
  cancelled:  'bg-red-100 text-red-600',
};

export default function MerchantDashboard() {
  const { user } = useApp();
  const [stats,        setStats]        = useState({ revenue: 0, orders: 0, products: 0, todayOrders: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [profile,      setProfile]      = useState(null);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        // ── Fetch only THIS merchant's sub-orders ──────────────
        const ordersQuery = query(
          collection(db, 'merchantOrders'),
          where('merchantId', '==', user.uid)
        );
        const ordersSnap  = await getDocs(ordersQuery);
        const myOrders    = ordersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ── Fetch only THIS merchant's products ────────────────
        const productsQuery = query(
          collection(db, 'products'),
          where('merchantId', '==', user.uid)
        );
        const productsSnap = await getDocs(productsQuery);

        // Today's orders
        const todayStart  = new Date(); todayStart.setHours(0, 0, 0, 0);
        const todayOrders = myOrders.filter((o) => {
          const d = o.createdAt?.toDate?.();
          return d && d >= todayStart;
        });

        // Revenue (exclude cancelled)
        const revenue = myOrders
          .filter((o) => o.status !== 'cancelled')
          .reduce((sum, o) => sum + (o.total || 0), 0);

        // Sort newest first
        const sorted = [...myOrders].sort((a, b) => {
          const da  = a.createdAt?.toDate?.() || new Date(0);
          const db_ = b.createdAt?.toDate?.() || new Date(0);
          return db_ - da;
        });

        setStats({
          revenue,
          orders:      myOrders.length,
          products:    productsSnap.size,
          todayOrders: todayOrders.length,
        });
        setRecentOrders(sorted.slice(0, 6));
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-rose-500 to-rose-700 rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
        <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full" />
        <div className="relative z-10">
          <p className="font-body text-rose-200 text-sm mb-1">Welcome back 👋</p>
          <h2 className="font-display font-bold text-2xl mb-1">{user?.displayName}</h2>
          <p className="text-rose-200 font-body text-sm">Here's your store performance today.</p>
        </div>
      </div>

      {/* Profile incomplete warning */}
      {!profile?.accountNumber && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <AlertCircle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-body font-semibold text-amber-700 text-sm">Complete your profile to receive payouts</p>
            <p className="font-body text-amber-600 text-xs mt-0.5">Add bank details and pickup address to start selling.</p>
          </div>
          <Link to="/merchant/profile"
            className="shrink-0 bg-amber-500 text-white text-xs font-body font-semibold px-3 py-1.5 rounded-xl hover:bg-amber-600 transition-colors">
            Complete →
          </Link>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="My Revenue"      value={`₹${stats.revenue.toLocaleString('en-IN')}`} sub="Excl. cancelled"  color="bg-rose-500"   />
        <StatCard icon={ShoppingBag} label="My Orders"       value={stats.orders}                                 sub="All time"         color="bg-blue-500"   />
        <StatCard icon={Package}     label="My Products"     value={stats.products}                               sub="Listed"           color="bg-purple-500" />
        <StatCard icon={TrendingUp}  label="Today's Orders"  value={stats.todayOrders}                            sub="Last 24 hrs"      color="bg-green-500"  />
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/merchant/products/add"
          className="flex items-center justify-between bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-rose-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-50 group-hover:bg-rose-500 rounded-xl flex items-center justify-center transition-colors">
              <Package size={20} className="text-rose-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <p className="font-body font-semibold text-gray-900 text-sm">Add New Product</p>
              <p className="text-gray-400 font-body text-xs">Upload photos, set price & stock</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-gray-300 group-hover:text-rose-500 group-hover:translate-x-1 transition-all" />
        </Link>

        <Link to="/merchant/orders"
          className="flex items-center justify-between bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-rose-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 group-hover:bg-blue-500 rounded-xl flex items-center justify-center transition-colors">
              <ShoppingBag size={20} className="text-blue-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <p className="font-body font-semibold text-gray-900 text-sm">My Orders</p>
              <p className="text-gray-400 font-body text-xs">View & update order status</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      {/* Recent orders */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-display font-semibold text-gray-900">Recent Orders</h3>
          <Link to="/merchant/orders"
            className="text-rose-500 hover:text-rose-600 font-body text-sm font-medium flex items-center gap-1 transition-colors">
            View All <ArrowRight size={14} />
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="p-10 text-center">
            <ShoppingBag size={36} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 font-body text-sm">No orders yet</p>
            <p className="text-gray-300 font-body text-xs mt-1">Orders for your products will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                <div className="flex -space-x-2 shrink-0">
                  {order.items?.slice(0, 2).map((item, i) => (
                    <img key={i} src={item.image} alt={item.name}
                      className="w-10 h-10 rounded-xl object-cover border-2 border-white" />
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-semibold text-gray-900 text-sm">#{order.id.slice(-6).toUpperCase()}</p>
                  <p className="text-gray-400 font-body text-xs truncate">
                    {order.shippingAddress?.name} · {order.items?.length} item(s)
                  </p>
                </div>
                <p className="font-display font-bold text-gray-900 text-sm shrink-0">
                  ₹{order.total?.toLocaleString('en-IN')}
                </p>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.confirmed}`}>
                  {order.status || 'confirmed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
